import { createContext, useContext, type ReactNode } from "react";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { useRosterAccessibleSections } from "./hooks/useRosterAccessibleSections";
import { resolveDefaultRosterSectionId } from "./resolveAccessibleSections";

type RosterAccessibleSectionsValue = ReturnType<typeof useRosterAccessibleSections>;

const RosterAccessibleSectionsContext = createContext<RosterAccessibleSectionsValue | null>(null);

export function RosterAccessibleSectionsProvider({ children }: { children: ReactNode }) {
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const enabled = flagsLoaded && flags["backoffice-section-roster-activity"] === true;
  const value = useRosterAccessibleSections(undefined, { enabled });

  return <RosterAccessibleSectionsContext.Provider value={value}>{children}</RosterAccessibleSectionsContext.Provider>;
}

export function useRosterAccessibleSectionsValue(preferredSectionId?: string | null): RosterAccessibleSectionsValue {
  const context = useContext(RosterAccessibleSectionsContext);
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const rosterFlagEnabled = flagsLoaded && flags["backoffice-section-roster-activity"] === true;
  const fallback = useRosterAccessibleSections(preferredSectionId, {
    enabled: context === null && rosterFlagEnabled
  });

  if (context) {
    return {
      sections: context.sections,
      defaultSectionId: resolveDefaultRosterSectionId(context.sections, preferredSectionId ?? context.defaultSectionId),
      canViewRosterActivity: context.canViewRosterActivity,
      loading: context.loading
    };
  }

  return fallback;
}
